import { useCallback, useEffect, useState } from 'react';
import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { courseKeysApi } from '@/lib/api/courseKeys';
import { CourseKeyEditorForm } from '@/components/course-keys/CourseKeyEditorForm';
import { CourseKeyListTab } from '@/components/course-keys/CourseKeyListTab';
import { CourseKeyReleaseTab } from '@/components/course-keys/CourseKeyReleaseTab';
import type { CourseKeyConfig, CourseKeyPlatform } from '@/types/course-key';
import { cn } from '@/lib/utils';

const TAB_VALUES = ['list', 'create', 'release'] as const;
type CourseKeyTab = (typeof TAB_VALUES)[number];

const PLATFORM_VALUES = ['all', 'ispring', 'mindtap'] as const;

export default function CourseKeysPage() {
  const [tab, setTab] = useQueryState(
    'tab',
    parseAsStringLiteral(TAB_VALUES).withDefault('list')
  );
  const [editId, setEditId] = useQueryState('editId', parseAsString);
  const [configId, setConfigId] = useQueryState('configId', parseAsString);
  const [listPlatform, setListPlatform] = useQueryState(
    'platform',
    parseAsStringLiteral(PLATFORM_VALUES).withDefault('all')
  );
  const [createPlatform, setCreatePlatform] = useQueryState(
    'createPlatform',
    parseAsStringLiteral(['ispring', 'mindtap'] as const).withDefault('ispring')
  );

  const [editing, setEditing] = useState<CourseKeyConfig | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const bumpRefresh = useCallback(() => {
    setRefreshToken((n) => n + 1);
  }, []);

  useEffect(() => {
    if (tab !== 'create' || !editId) {
      if (!editId) setEditing(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingEdit(true);
        const config = await courseKeysApi.getById(editId);
        if (!cancelled) setEditing(config);
      } catch {
        if (!cancelled) {
          setEditing(null);
          void setEditId(null);
        }
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, editId, setEditId]);

  const switchTab = (next: CourseKeyTab) => {
    void setTab(next);
  };

  const openCreate = (platform: CourseKeyPlatform = 'ispring') => {
    void setEditId(null);
    void setCreatePlatform(platform);
    setEditing(null);
    void setTab('create');
  };

  const openEdit = (config: CourseKeyConfig) => {
    void setEditId(config._id);
    setEditing(config);
    void setTab('create');
  };

  const handleCancelCreate = () => {
    void setEditId(null);
    setEditing(null);
    void setTab('list');
  };

  const handleSubmitSuccess = ({
    configId: savedConfigId,
    platform,
    status,
  }: {
    configId: string;
    platform: CourseKeyPlatform;
    status: 'draft' | 'active';
  }) => {
    void setEditId(null);
    setEditing(null);
    bumpRefresh();

    if (status === 'active' && platform === 'mindtap') {
      void setConfigId(savedConfigId);
      void setTab('release');
      return;
    }

    if (status === 'active' && platform === 'ispring') {
      void setConfigId(null);
      void setTab('list');
      return;
    }

    void setTab('list');
  };

  const tabButtons: { id: CourseKeyTab; label: string }[] = [
    { id: 'create', label: 'Create Course Keys' },
    { id: 'list', label: 'List View' },
    { id: 'release', label: 'Release Key' },
  ];

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-muted-foreground">Admin / Course Key</p>
          <h1 className="text-2xl font-semibold tracking-tight">Course Key</h1>
        </div>
        <Button onClick={() => openCreate('ispring')}>
          <Plus className="mr-2 h-4 w-4" />
          Create Course Key
        </Button>
      </div>

      <div className="flex gap-1 border-b">
        {tabButtons.map((btn) => (
          <button
            key={btn.id}
            type="button"
            onClick={() => switchTab(btn.id)}
            className={cn(
              'border-b-2 px-4 py-3 text-sm font-medium transition-colors -mb-px',
              tab === btn.id
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {tab === 'create' && (
        loadingEdit && editId ? (
          <p className="text-sm text-muted-foreground">Loading course key…</p>
        ) : (
          <CourseKeyEditorForm
            active
            editing={editing}
            initialPlatform={createPlatform}
            onSaved={bumpRefresh}
            onSubmitSuccess={handleSubmitSuccess}
            onCancel={handleCancelCreate}
          />
        )
      )}

      <CourseKeyListTab
        active={tab === 'list'}
        platformFilter={listPlatform}
        onPlatformFilterChange={(f) => void setListPlatform(f)}
        onEdit={openEdit}
        onCreateClick={() => openCreate('ispring')}
        refreshToken={refreshToken}
      />

      <CourseKeyReleaseTab
        active={tab === 'release'}
        configIdFilter={configId}
        refreshToken={refreshToken}
        onClearConfigFilter={() => void setConfigId(null)}
      />
    </div>
  );
}
